import { useState, useRef, useEffect } from "react";
import "./CBS2.css";

interface CBS2Props {
  onComplete: (packageType: "single" | "five-pack", amount: number) => void;
  onDecline: () => void;
}

type PackageType = "single" | "five-pack" | null;

export default function CBS2({ onComplete, onDecline }: CBS2Props) {
  const [selectedPackage, setSelectedPackage] = useState<PackageType>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const confettiRef = useRef<HTMLDivElement>(null);

  const handleContinue = () => {
    if (!selectedPackage) return;
    setSubmitted(true);
    setShowConfetti(true);

    // Fire completion after confetti starts
    setTimeout(() => {
      const amount = selectedPackage === "five-pack" ? 100 : 20;
      onComplete(selectedPackage, amount);
    }, 1500);
  };

  // Generate confetti particles
  useEffect(() => {
    if (!showConfetti || !confettiRef.current) return;

    const container = confettiRef.current;
    const colors = [
      "#E8544F",
      "#faf8f3",
      "#FFD700",
      "#4CAF50",
      "#FF6B6B",
      "#845EC2",
    ];

    for (let i = 0; i < 80; i++) {
      const particle = document.createElement("div");
      particle.className = "confetti-particle";
      particle.style.setProperty("--x", `${(Math.random() - 0.5) * 600}px`);
      particle.style.setProperty("--y", `${-Math.random() * 800 - 200}px`);
      particle.style.setProperty("--r", `${Math.random() * 720 - 360}deg`);
      particle.style.setProperty("--delay", `${Math.random() * 0.3}s`);
      particle.style.setProperty("--duration", `${1 + Math.random() * 1.5}s`);
      particle.style.backgroundColor =
        colors[Math.floor(Math.random() * colors.length)];
      particle.style.width = `${4 + Math.random() * 8}px`;
      particle.style.height = `${4 + Math.random() * 8}px`;

      if (Math.random() > 0.5) {
        particle.style.borderRadius = "50%";
      }

      container.appendChild(particle);
    }

    return () => {
      container.innerHTML = "";
    };
  }, [showConfetti]);

  return (
    <div className="cbs2-container">
      <div className={`cbs2-form-area ${submitted ? "celebrated" : ""}`}>
        <div className="cbs2-header">
          <div className="cbs2-label">PRE-BOOK YOUR FIRST SESSION</div>
          <h2>Be one of the first in.</h2>
          <p className="cbs2-sub">
            Reserve your spot at founding member pricing. No payment taken today
            - just showing us you're serious.
          </p>
        </div>

        <div className="package-cards">
          <button
            className={`package-card ${
              selectedPackage === "single" ? "selected" : ""
            }`}
            onClick={() => setSelectedPackage("single")}
            disabled={submitted}
          >
            <div className="package-badge">SINGLE</div>
            <div className="package-price">£20</div>
            <div className="package-desc">One founding member session</div>
            <div className="package-perk">First session at launch</div>
            {selectedPackage === "single" && (
              <div className="package-check">✓</div>
            )}
          </button>

          <button
            className={`package-card featured ${
              selectedPackage === "five-pack" ? "selected" : ""
            }`}
            onClick={() => setSelectedPackage("five-pack")}
            disabled={submitted}
          >
            <div className="package-popular">MOST POPULAR</div>
            <div className="package-badge">FIVE-PACK</div>
            <div className="package-price">£100</div>
            <div className="package-desc">Five founding member sessions</div>
            <div className="package-perk">Priority access + OG perks</div>
            {selectedPackage === "five-pack" && (
              <div className="package-check">✓</div>
            )}
          </button>
        </div>

        <div className="cbs2-action-area">
          <div className="confetti-anchor" ref={confettiRef} />
          <button
            className={`cbs2-continue ${selectedPackage ? "active" : ""} ${
              submitted ? "submitted" : ""
            }`}
            onClick={handleContinue}
            disabled={!selectedPackage || submitted}
          >
            {submitted ? "You're in! 🎉" : "Continue to Payment"}
          </button>
        </div>

        <button
          className="cbs2-decline"
          onClick={onDecline}
          disabled={submitted}
        >
          Not ready to commit yet
        </button>

        <div className="cbs2-reassurance">
          <p>🔒 Secure checkout · Cancel anytime before launch</p>
        </div>
      </div>
    </div>
  );
}
